import {
  updatingClass,
  formatPrice,
  calculateServiceCharges,
  calculatePortFees,
} from '../cruise-calculation-common';

export default (totalPrice, childPrice, juniorPrice, nights = 0) => ({
  adults: 2,
  adultsList: 2,
  cabinPrice: {}, // This stores the entire prices object
  cabinPriceSlow: 0,
  cabinPriceUpdating: false,
  child: 0,
  childList: 0,
  height: 0,
  junior: 0,
  juniorList: 0,
  nights: nights,
  selectedCabinName: '', // Defaultní hodnota pro název kajuty
  cabinName: '',
  cabinDeck: '',
  cabinType: '',
  taxesUpdating: false,
  showSidebar: false,
  insuranceUpdating: false,
  totalInsuranceUpdating: false,
  insurance: null,
  insuranceSlow: null,
  trips: [],
  tripsSlow: [],
  tripsPrice: 0,
  tripsUpdating: false,
  drinks: [],
  drinksSlow: [],
  drinksUpdating: false,
  drinksPrice: 0,
  totalDrinksUpdating: false,
  adultInfo1: {},
  adultInfo2: {},
  childInfo1: {},
  childInfo2: {},
  juniorInfo1: {},
  juniorInfo2: {},

  // Variables to store previous valid values
  validAdults: 2,
  validChild: 0,
  validJunior: 0,

  adultsSlow: 2,
  childSlow: 0,
  juniorSlow: 0,

  showInvalidPassengerWarning: false,

  init() {
    // Watcher for adults
    this.$watch('adults', async (value, oldValue) => {
      if (this.isCombinationValid(value, this.child, this.junior)) {
        setTimeout(() => {
          this.adultsSlow = value;
          this.calculateDrinksPrice();
        }, 200);

        if (oldValue < value) {
          this.adultsList = value;
        } else {
          this.$data['adultInfo' + oldValue] = {};
          setTimeout(() => {
            this.adultsList = value;
          }, 200);
        }

        // Trigger animation only if value differs from last valid value
        if (value !== this.validAdults) {
          await this.handleAnimation('cabinPriceUpdating');
        }

        // Update valid value on valid change
        this.validAdults = value;
      } else {
        this.showInvalidPassengerWarning = true;
        this.adults = this.validAdults;
        this.adultsSlow = this.validAdults;
      }
    });

    // Watcher for child
    this.$watch('child', async (value, oldValue) => {
      if (this.isCombinationValid(this.adults, value, this.junior)) {
        setTimeout(() => {
          this.childSlow = value;
        }, 200);

        if (oldValue < value) {
          this.childList = value;
        } else {
          this.$data['childInfo' + oldValue] = {};
          setTimeout(() => {
            this.childList = value;
          }, 200);
        }

        if (value !== this.validChild) {
          await this.handleAnimation('cabinPriceUpdating');
        }

        this.validChild = value;
      } else {
        this.showInvalidPassengerWarning = true;
        this.child = this.validChild;
        this.childSlow = this.validChild;
      }
    });

    // Watcher for junior
    this.$watch('junior', async (value, oldValue) => {
      if (this.isCombinationValid(this.adults, this.child, value)) {
        setTimeout(() => {
          this.juniorSlow = value;
        }, 200);

        if (oldValue < value) {
          this.juniorList = value;
        } else {
          this.$data['juniorInfo' + oldValue] = {};
          setTimeout(() => {
            this.juniorList = value;
          }, 200);
        }

        if (value !== this.validJunior) {
          await this.handleAnimation('cabinPriceUpdating');
        }

        this.validJunior = value;
      } else {
        this.showInvalidPassengerWarning = true;
        this.junior = this.validJunior;
        this.juniorSlow = this.validJunior;
      }
    });

    // Watcher for the warning display
    this.$watch('showInvalidPassengerWarning', (value) => {
      if (value) {
        setTimeout(() => {
          this.showInvalidPassengerWarning = false;
        }, 7000); // Hide the warning after 10 seconds
      }
    });

    this.$watch('trips', (value, oldValue) => {
      if (value.length > oldValue.length) {
        this.tripsSlow = value;
      } else {
        setTimeout(() => {
          this.tripsSlow = value;
        }, 200);
      }
      this.handleAnimation('tripsUpdating');
      setTimeout(() => {
        this.calculateTripsPrice();
      }, 200);
    });

    this.$watch('drinks', (value, oldValue) => {
      const drinks = value.map((json) => JSON.parse(json));
      if (value.length > oldValue.length) {
        this.drinksSlow = drinks;
      } else {
        setTimeout(() => {
          this.drinksSlow = drinks;
        }, 200);
      }
      this.handleAnimation('drinksUpdating');
      setTimeout(() => {
        this.calculateDrinksPrice();
      }, 200);
    });

    this.$watch('sent', () => {
      if (!this.failed) {
        this.resetCalculation();
      }
    });
  },

  taxesPriceClass() {
    return updatingClass(this.taxesUpdating);
  },

  cabinPriceClass() {
    return updatingClass(this.cabinPriceUpdating);
  },

  insurancePriceClass() {
    return updatingClass(this.cabinPriceUpdating || this.insuranceUpdating);
  },

  drinksPriceClass() {
    return updatingClass(this.cabinPriceUpdating || this.drinksUpdating);
  },

  // Getter to calculate total service charges
  get totalServiceCharges() {
    return this.calculateServiceCharges(
      this.adultsSlow,
      this.childSlow,
      this.juniorSlow,
      this.nights
    );
  },

  // Method to calculate service charges
  calculateServiceCharges(adults, children, juniors, nights) {
    const { cabinPriceSlow } = this;

    return calculateServiceCharges({
      cabinPriceSlow,
      adults,
      children,
      juniors,
      nights,
    });
  },

  // Getter to calculate total fees (port fees + service charges)
  get totalFees() {
    if (this.cabinPriceSlow == -1) {
      return 0;
    }
    return this.cabinPriceSlow;
  },

  // Getter to calculate total cruise price without fees (ensures it returns a number)
  get cruisePrice() {
    const { adultsSlow, childSlow, juniorSlow } = this;

    // Always charge for 2 adults if there's at least 1 adult
    let price = 2 * totalPrice;

    // If we have only 1 adult, we need to handle the second adult slot
    if (adultsSlow === 1) {
      // If there's a junior, use it as second adult
      if (juniorSlow > 0) {
        // Remove one junior from the count since it's used as second adult
        price += (juniorSlow - 1) * juniorPrice;

        // Add all children as they are not used as second adult
        price += childSlow * childPrice;
      }
      // If no junior but there's a child, use it as second adult
      else if (childSlow > 0) {
        // Add remaining children (if any)
        price += (childSlow - 1) * childPrice;
      }
    } else {
      // If we have 2 or more adults, just add all children and juniors
      price += childSlow * childPrice + juniorSlow * juniorPrice;
    }

    return Number(price || 0);
  },

  adultPrice(price) {
    return this.childSlow === 0 &&
      this.juniorSlow === 0 &&
      this.adultsSlow === 1
      ? price * 2
      : price;
  },

  childPrice(price, index) {
    if (index === 0 && this.juniorSlow === 0 && this.adultsSlow === 1) {
      return totalPrice;
    } else {
      return price;
    }
  },

  juniorPrice(price, index) {
    if (index === 0 && this.adultsSlow === 1) {
      return totalPrice;
    } else {
      return price;
    }
  },

  // Getter to calculate final total price (cruisePrice + all fees)
  get totalPrice() {
    let insurancePrice = 0;
    if (this.insuranceSlow) {
      insurancePrice +=
        this.insuranceSlow.price * this.adultsSlow +
        this.insuranceSlow.priceChild * (this.childSlow + this.juniorSlow);
    }

    return (
      this.cruisePrice +
      this.totalFees +
      insurancePrice +
      this.tripsPrice +
      this.drinksPrice
    );
  },

  get tripNames() {
    if (!this.trips || this.trips.length === 0) {
      return '';
    }
    return this.trips
      .map((json) => JSON.parse(json)) // Parse each JSON string into an object
      .map((obj) => obj.title) // Extract the title from each object
      .join(', '); // Join titles with ", "
  },

  get drinkPackagesNames() {
    if (!this.drinks) {
      return '';
    }
    return this.drinks
      .map((json) => JSON.parse(json)) // Parse each JSON string into an object
      .map((obj) => obj.title) // Extract the title from each object
      .join(', '); // Join titles with ", "
  },

  // Getter to calculate total port fees
  get totalPortFees() {
    return this.calculatePortFees(
      this.adultsSlow,
      this.childSlow,
      this.juniorSlow
    );
  },

  // Method to calculate port fees
  calculatePortFees(adults, children, juniors) {
    const { cabinPriceSlow } = this;

    return calculatePortFees({
      adults,
      children,
      juniors,
      cabinPriceSlow,
    });
  },

  formatPrice(price, included = false) {
    const zeroTranslations = {
      included: this.$t('price_included'),
      free: this.$t('free'),
      onInquiry: this.$t('on_inquiry'),
    };

    return formatPrice({
      price,
      included,
      zeroTranslations,
    });
  },

  async handleAnimation(variable) {
    this[variable] = true;
    await new Promise((resolve) => setTimeout(resolve, 500)); // Wait for animation to finish (500ms)
    this[variable] = false;
  },

  // Method to set the price when the cabin changes
  async setCabin(newPrices, cabinName, cabinType) {
    this.showInvalidPassengerWarning = false;
    //await this.handleAnimation('cabinPriceUpdating'); // Trigger animation first
    await this.handleAnimation('taxesUpdating');
    this.cabinPrice = newPrices;
    this.cabinPriceSlow = newPrices;

    // Update the selected cabin name
    this.selectedCabinName = cabinName || this.$t('inside_cabin');

    this.cabinType = cabinType || {
      class: 'fantastica',
      label: 'FANTASTICA Experience',
    };
  },

  // Method to validate the combination of crew
  isCombinationValid(adults, child, junior) {
    if (adults < 3 && child < 3 && junior < 3 && child + junior < 3) {
      return true;
    }
    return false;
  },

  handleInsurance(insurance) {
    if (JSON.stringify(this.insurance) == JSON.stringify(insurance)) {
      this.insurance = null;
      setTimeout(() => {
        this.insuranceSlow = null;
      }, 200);
      this.$el.checked = false;
    } else {
      this.handleAnimation('insuranceUpdating');
      setTimeout(() => {
        this.insuranceSlow = insurance;
      }, 200);
      this.insurance = insurance;
    }
    this.handleAnimation('totalInsuranceUpdating');
  },

  get insurancePrice() {
    let price = 0;
    if (this.insuranceSlow) {
      price +=
        this.insuranceSlow.price * this.adultsSlow +
        this.insuranceSlow.priceChild * (this.childSlow + this.juniorSlow);
    }
    return this.formatPrice(price);
  },

  calculateTripsPrice() {
    this.tripsPrice = this.trips.reduce((total, trip) => {
      const { price } = JSON.parse(trip);
      const totalPassengers =
        this.adultsSlow + this.childSlow + this.juniorSlow;
      return total + price * totalPassengers;
    }, 0);
  },

  calculateDrinksPrice() {
    this.drinksPrice = this.drinksSlow.reduce((total, drink) => {
      const { price, priceChild } = drink;
      return (
        total +
        price * this.adultsSlow +
        priceChild * (this.childSlow + this.juniorSlow)
      );
    }, 0);
  },

  resetCalculation() {
    this.adults = 2;
    this.adultsList = 2;
    this.cabinPrice = {}; // This stores the entire prices object
    this.cabinPriceSlow = 0;
    this.cabinPriceUpdating = false;
    this.child = 0;
    this.childList = 0;
    this.height = 0;
    this.junior = 0;
    this.juniorList = 0;
    this.nights = nights;
    this.selectedCabinName = ''; // Defaultní hodnota pro název kajuty
    this.cabinName = '';
    this.cabinType = '';
    this.taxesUpdating = false;
    this.showSidebar = false;
    this.insuranceUpdating = false;
    this.totalInsuranceUpdating = false;
    this.insurance = [];
    this.insuranceSlow = [];
    this.trips = [];
    this.tripsSlow = [];
    this.tripsPrice = 0;
    this.tripsUpdating = false;
    this.drinks = [];
    this.drinksSlow = [];
    this.drinksPrice = 0;
    this.drinksUpdating = false;
    // Variables to store previous valid values
    this.validAdults = 2;
    this.validChild = 0;
    this.validJunior = 0;

    this.adultsSlow = 2;
    this.childSlow = 0;
    this.juniorSlow = 0;

    this.showInvalidPassengerWarning = false;
  },

  //Method to prevent errors in console from using the same cabin partial for both individual and delegate
  cabinClass(prices) {
    return;
  },
});
